'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';
import { css, cx } from 'styled-system/css';

import { Menu } from '@shaojiang/components/ui/menu';
import type { SiteCopy } from '@shaojiang/content';
import { DESIGN_IDS, DESIGN_QUERY_PARAM, isDesignId } from '@shaojiang/designs/config';
import { designList, designs } from '@shaojiang/designs/registry';
import { DesignVariants } from '@shaojiang/designs/runtime/DesignOnly';
import { useDesign } from '@shaojiang/designs/runtime/useDesign';
import useLocale from '@shaojiang/hooks/useLocale';

import { DesignHint } from './DesignHint';

interface Props {
  labels: Pick<SiteCopy['nav'], 'switchDesign' | 'designHint' | 'designNudge' | 'dismiss'>;
}

/** Remembers that the visitor has seen (or used) the switch, so the nudge shows only once */
const HINT_KEY = 'shaojiang-design-hint';
const HINT_DELAY = 3500;
const HINT_DURATION = 14000;

const trigger = css({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  h: '10',
  pl: '2',
  pr: '3',
  borderRadius: 'full',
  border: 'none',
  bg: 'bg.glass',
  color: 'fg.default',
  boxShadow: 'control',
  cursor: 'pointer',
  textStyle: 'xs',
  fontWeight: '700',
  whiteSpace: 'nowrap',
  _focusVisible: { boxShadow: 'focus' },
  mdDown: { pr: '2' },
  _nebula: {
    h: '9',
    borderRadius: 'l3',
    fontWeight: '500',
    border: '1px solid {colors.border.default}',
    backdropFilter: 'blur({blurs.glass})',
    _hover: { borderColor: 'fg.accent' },
  },
  _bento: { bg: 'bg.surface', boxShadow: 'none' },
});

const dot = css({
  display: 'block',
  w: '4',
  h: '4',
  borderRadius: 'full',
  boxShadow: '0 0 0 2px {colors.bg.surface}',
  transition: 'translate {durations.base} {easings.spring}',
  '& + &': { ml: '-1.5' },
  _groupHover: {
    '&:nth-child(1)': { translate: '-3px -2px' },
    '&:nth-child(3)': { translate: '3px 2px' },
  },
  _nebula: { w: '3.5', h: '3.5' },
});

/** Three paint dots for a design */
function Swatches({ colors }: { colors: readonly string[] }) {
  return (
    <span className={css({ display: 'flex' })} aria-hidden>
      {colors.map((color) => (
        <span key={color} className={dot} style={{ background: color }} />
      ))}
    </span>
  );
}

/** A tiny page in the design's own colors and display font */
function Preview({ id }: { id: keyof typeof designs }) {
  const { preview, swatches } = designs[id];
  return (
    <span
      aria-hidden
      className={css({
        position: 'relative',
        flexShrink: '0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        w: '16',
        h: '12',
        p: '1.5',
        borderRadius: 'l2',
        overflow: 'hidden',
        boxShadow: 'card',
      })}
      style={{ background: preview.canvas, color: preview.ink }}
    >
      <span style={{ fontFamily: preview.font, fontSize: 17, lineHeight: 1 }}>Aa</span>
      <span className={css({ display: 'flex', gap: '1' })}>
        {swatches.map((color) => (
          <span
            key={color}
            className={css({ w: '2.5', h: '2.5', borderRadius: 'full' })}
            style={{ background: color }}
          />
        ))}
      </span>
    </span>
  );
}

/**
 * Lets visitors re-skin the whole site. The pill shows the current design (its colors and name,
 * rendered for every design so there's no flicker on load). On a first visit it pings and wiggles
 * and a nudge pops in; picking a design reveals it in a circle growing from the pill.
 */
export function DesignSwitch({ labels }: Props) {
  const { id, setDesign } = useDesign();
  const { locale } = useLocale();
  const zh = locale === 'zh';
  const reduceMotion = useReducedMotion();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [hint, setHint] = useState(false);

  const markSeen = () => {
    setHint(false);
    try {
      localStorage.setItem(HINT_KEY, '1');
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    let seen = true;
    try {
      seen =
        !!localStorage.getItem(HINT_KEY) ||
        new URLSearchParams(location.search).has(DESIGN_QUERY_PARAM);
    } catch {
      // no storage: don't nag
    }
    if (seen) return;
    const showTimer = setTimeout(() => setHint(true), HINT_DELAY);
    const hideTimer = setTimeout(markSeen, HINT_DELAY + HINT_DURATION);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  const pick = (value: string) => {
    if (!isDesignId(value)) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    setDesign(value, rect && { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
  };

  const name = (designId: keyof typeof designs) =>
    zh ? designs[designId].nameZh : designs[designId].name;

  return (
    <span className={css({ position: 'relative', display: 'inline-flex' })}>
      <Menu.Root
        positioning={{ placement: 'bottom-end', gutter: 10 }}
        open={open}
        onOpenChange={(details) => {
          setOpen(details.open);
          if (details.open) markSeen();
        }}
        onSelect={({ value }) => pick(value)}
      >
        <Menu.Trigger asChild>
          <m.button
            ref={triggerRef}
            type='button'
            aria-label={labels.switchDesign}
            title={labels.switchDesign}
            className={cx('group', trigger)}
            animate={
              hint && !reduceMotion
                ? { rotate: [0, -7, 7, -4, 4, 0], scale: [1, 1.06, 1] }
                : { rotate: 0, scale: 1 }
            }
            transition={
              hint ? { duration: 0.9, repeat: Infinity, repeatDelay: 2.2 } : { duration: 0.2 }
            }
            whileTap={{ scale: 0.95 }}
          >
            {/* a soft ping behind the pill while the nudge is up */}
            {hint && !reduceMotion && (
              <m.span
                aria-hidden
                className={css({
                  position: 'absolute',
                  inset: '0',
                  zIndex: '-1',
                  borderRadius: 'inherit',
                  bg: 'hot.default',
                  _nebula: { bgGradient: 'brand' },
                })}
                initial={{ opacity: 0.5, scale: 1 }}
                animate={{ opacity: 0, scale: 1.6 }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
              />
            )}
            <DesignVariants
              as='span'
              {...Object.fromEntries(
                DESIGN_IDS.map((designId) => [
                  designId,
                  <Swatches key={designId} colors={designs[designId].swatches} />,
                ]),
              )}
            />
            <span className={css({ mdDown: { display: 'none' } })}>
              <DesignVariants
                as='span'
                {...Object.fromEntries(DESIGN_IDS.map((designId) => [designId, name(designId)]))}
              />
            </span>
            <m.span
              animate={{ rotate: open ? 180 : 0 }}
              className={css({
                display: 'inline-flex',
                color: 'fg.muted',
                '& svg': { w: '3.5', h: '3.5' },
                mdDown: { display: 'none' },
              })}
            >
              <ChevronDown />
            </m.span>
          </m.button>
        </Menu.Trigger>
        <Menu.Positioner>
          <Menu.Content minW='76' p='2'>
            <Menu.ItemGroup>
              <Menu.ItemGroupLabel textStyle='label' color='fg.subtle' px='3' pt='2' pb='1'>
                {labels.switchDesign}
              </Menu.ItemGroupLabel>
              {designList.map((design) => {
                const selected = design.id === id;
                return (
                  <Menu.Item
                    key={design.id}
                    value={design.id}
                    h='auto'
                    py='2.5'
                    px='2.5'
                    gap='3'
                    borderRadius='l2'
                    aria-checked={selected}
                    bg={selected ? 'brand.subtle' : undefined}
                  >
                    <Preview id={design.id} />
                    <span
                      className={css({
                        display: 'flex',
                        flexDirection: 'column',
                        flex: '1',
                        minW: '0',
                      })}
                    >
                      <span className={css({ fontWeight: '700', color: 'fg.default' })}>
                        {zh ? design.nameZh : design.name}
                      </span>
                      <span className={css({ textStyle: 'xs', color: 'fg.muted' })}>
                        {zh ? design.descriptionZh : design.description}
                      </span>
                    </span>
                    {selected && (
                      <Check
                        className={css({ color: 'fg.accent', w: '4', h: '4', flexShrink: '0' })}
                      />
                    )}
                  </Menu.Item>
                );
              })}
            </Menu.ItemGroup>
            <p className={css({ textStyle: 'xs', color: 'fg.subtle', px: '3', pt: '2', pb: '1' })}>
              {labels.designHint}
            </p>
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
      <DesignHint
        show={hint && !open}
        text={labels.designNudge}
        dismissLabel={labels.dismiss}
        onDismiss={markSeen}
      />
    </span>
  );
}
